// src/pages/offlineSales/ChequesPage.jsx
//
// Register of every cheque received through Offline Sales (per-invoice payments and customer
// receipts). Defaults to the "Depositable now" view: pending cheques whose date has arrived.
// Backend: rettzy-backend/src/modules/offlineSales/services/cheque.service.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Banknote, ArrowDownToLine, CircleCheck, Ban, CalendarClock, User, Phone, RefreshCw,
} from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getCheques, getChequeSummary, updateChequeStatus } from '../../services/api/offlineSales/cheques';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate } from '../../utils/dateUtils';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './ChequesPage.module.css';

const VIEWS = ['depositable', 'post_dated', 'deposited', 'cleared', 'bounced', 'cancelled', 'all'];

const STATUS_CLASS = {
  pending: 'statusPending',
  deposited: 'statusDeposited',
  cleared: 'statusCleared',
  bounced: 'statusBounced',
  cancelled: 'statusCancelled',
};

const daysBetween = (fromYMD, toYMD) => {
  const a = new Date(`${String(fromYMD).slice(0, 10)}T00:00:00`);
  const b = new Date(`${String(toYMD).slice(0, 10)}T00:00:00`);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

const ChequesPage = () => {
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('edit_offline_sales_order');

  const [view, setView] = useState('depositable');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [cheques, setCheques] = useState([]);
  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0, pageSizeOptions: [20, 50, 100] });

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await getChequeSummary());
    } catch {
      // summary cards are informational — the table still works without them
    }
  }, []);

  const loadCheques = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCheques({
        view: view === 'all' ? undefined : view,
        search: search.trim() || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        page: pagination.currentPage,
        limit: pagination.pageSize,
      });
      setCheques(result.cheques || []);
      setToday(result.today || '');
      setPagination((p) => ({ ...p, total: result.pagination?.total || 0 }));
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.chqFailedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [view, search, filters, pagination.currentPage, pagination.pageSize, showError, t]);

  useEffect(() => { loadCheques(); }, [loadCheques]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const changeView = (next) => {
    setView(next);
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handleStatusChange = async (cheque, status) => {
    if (status === 'bounced') {
      const confirmed = await confirm(t('offlineSales.chqBounceConfirm'), { type: 'danger' });
      if (!confirmed) return;
    }
    if (status === 'deposited') {
      const confirmed = await confirm(t('offlineSales.chqDepositConfirm'), { type: 'warning' });
      if (!confirmed) return;
    }
    setBusyId(cheque.id);
    try {
      await updateChequeStatus(cheque.id, status);
      success(t('offlineSales.chqStatusUpdated'));
      await Promise.all([loadCheques(), loadSummary()]);
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.chqFailedToUpdate'));
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: 'chequeDate',
      header: t('offlineSales.chqDate'),
      render: (row) => {
        const daysLeft = today && row.status === 'pending' ? daysBetween(today, row.chequeDate) : null;
        return (
          <div className={styles.dateCell}>
            <span className={styles.dateValue}><CalendarClock size={14} />{formatDate(row.chequeDate)}</span>
            {row.isPostDated && <span className={styles.pdcTag}>{t('offlineSales.chqPdcTag')}</span>}
            {daysLeft > 0 && <span className={styles.dueChip}>{t('offlineSales.chqDueInDays', { days: daysLeft })}</span>}
          </div>
        );
      },
    },
    {
      key: 'customer',
      header: t('offlineSales.customer'),
      render: (row) => (row.customer ? (
        <div className={tb.partyInfo}>
          <div className={tb.partyName}><User size={14} />{row.customer.name}</div>
          {row.customer.phone && <div className={tb.partyMeta}><Phone size={12} />{row.customer.phone}</div>}
        </div>
      ) : <span className={tb.naText}>—</span>),
    },
    {
      key: 'linkedTo',
      header: t('offlineSales.chqLinkedTo'),
      render: (row) => {
        if (row.order) {
          return <Link className={tb.docLink} to={`/offline-sales/orders/${row.order.id}`}>{row.order.orderNumber}</Link>;
        }
        if (row.customerPayment) {
          return <Link className={tb.docLink} to={`/offline-sales/customer-payments?highlight=${row.customerPayment.id}`}>{row.customerPayment.paymentNumber}</Link>;
        }
        return <span className={tb.naText}>—</span>;
      },
    },
    {
      key: 'amount',
      header: t('offlineSales.amount'),
      align: 'right',
      render: (row) => <span className={tb.totalAmount}>{formatCurrency(row.amount)}</span>,
    },
    {
      key: 'status',
      header: t('offlineSales.statusCol'),
      render: (row) => (
        <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[row.status]] || ''}`}>
          {t(`offlineSales.chqStatus_${row.status}`)}
        </span>
      ),
    },
  ];

  const renderActions = (row) => {
    if (!canUpdate) return null;
    const chequeYMD = String(row.chequeDate || '').slice(0, 10);
    const notDue = Boolean(today && chequeYMD > today);
    const busy = busyId === row.id;
    const depositTitle = notDue
      ? t('offlineSales.chqNotYetDueOn', { date: formatDate(chequeYMD), days: daysBetween(today, chequeYMD) })
      : t('offlineSales.chqMarkDeposited');
    return (
      <div className={tb.actionsWrapper}>
        {row.status === 'pending' && (
          <span className={notDue ? tb.disabledActionWrap : undefined} title={depositTitle}>
            <button
              className={`${tb.actionButton} ${tb.viewButton}`}
              onClick={() => handleStatusChange(row, 'deposited')}
              disabled={busy || notDue}
              aria-label={depositTitle}
            >
              <ArrowDownToLine size={14} />
            </button>
          </span>
        )}
        {row.status === 'deposited' && (
          <button
            className={`${tb.actionButton} ${tb.actionSuccess}`}
            onClick={() => handleStatusChange(row, 'cleared')}
            disabled={busy}
            title={t('offlineSales.chqMarkCleared')}
          >
            <CircleCheck size={14} />
          </button>
        )}
        {['pending', 'deposited'].includes(row.status) && (
          <button
            className={`${tb.actionButton} ${tb.actionDanger}`}
            onClick={() => handleStatusChange(row, 'bounced')}
            disabled={busy}
            title={t('offlineSales.chqMarkBounced')}
          >
            <Ban size={14} />
          </button>
        )}
      </div>
    );
  };

  const cards = [
    { key: 'depositableNow', view: 'depositable', icon: ArrowDownToLine, label: t('offlineSales.chqView_depositable') },
    { key: 'postDatedPending', view: 'post_dated', icon: CalendarClock, label: t('offlineSales.chqView_post_dated') },
    { key: 'deposited', view: 'deposited', icon: Banknote, label: t('offlineSales.chqView_deposited') },
    { key: 'bounced', view: 'bounced', icon: Ban, label: t('offlineSales.chqView_bounced') },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.chqTitle')}
        description={t('offlineSales.chqDescription')}
        icon={Banknote}
        actions={
          <button className={styles.refreshBtn} onClick={() => { loadCheques(); loadSummary(); }} title={t('common.refresh', 'Refresh')}>
            <RefreshCw size={16} />
          </button>
        }
      />

      <div className={styles.summaryCards}>
        {cards.map((card) => {
          const Icon = card.icon;
          const data = summary?.[card.key];
          return (
            <button
              type="button"
              key={card.key}
              className={`${styles.summaryCard} ${view === card.view ? styles.summaryCardActive : ''}`}
              onClick={() => changeView(card.view)}
            >
              <div className={styles.summaryCardLabel}><Icon size={14} />{card.label}</div>
              <div className={styles.summaryCardValue}>{formatCurrency(data?.amount || 0)}</div>
              <div className={styles.summaryCardMeta}>{t('offlineSales.chqCount', { count: data?.count || 0 })}</div>
            </button>
          );
        })}
      </div>

      <div className={styles.viewTabs} role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            className={`${styles.viewTab} ${view === v ? styles.viewTabActive : ''}`}
            onClick={() => changeView(v)}
          >
            {t(`offlineSales.chqView_${v}`)}
          </button>
        ))}
      </div>

      <FilterPanel
        filters={[{ key: 'date', type: 'daterange', label: t('offlineSales.chqDateRange') }]}
        values={filters}
        onChange={(next) => {
          setFilters({ dateFrom: next.dateFrom || '', dateTo: next.dateTo || '' });
          setPagination((p) => ({ ...p, currentPage: 1 }));
        }}
        onClear={() => setSearch('')}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: search,
          onChange: (value) => { setSearch(value); setPagination((p) => ({ ...p, currentPage: 1 })); },
          placeholder: t('offlineSales.chqSearchPlaceholder'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: (size) => setPagination((p) => ({ ...p, pageSize: size, currentPage: 1 })),
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={cheques}
        columns={columns}
        actions={canUpdate ? { render: renderActions, width: '130px' } : undefined}
        loading={loading}
        pagination={pagination}
        onPaginationChange={(changes) => setPagination((p) => ({ ...p, ...changes }))}
        emptyMessage={t('offlineSales.chqEmpty')}
        emptyDescription={t('offlineSales.chqEmptyDescription')}
        emptyIcon={<Banknote size={64} />}
      />
    </div>
  );
};

export default ChequesPage;
